import { useEffect, useRef, useState } from 'react';
import type { Attachment, Claim } from '../types';
import { EVIDENCE } from '../evidence';
import { PMS } from '@/features/shared/pms';

export function resolveAttachment(claim: Claim, id: string): Attachment | undefined {
  const inList = claim.attachments.find((a) => a.id === id);
  if (inList) return inList;
  if (claim.decision) {
    for (const o of claim.decision.options) {
      if (o.attachment?.id === id) return o.attachment;
    }
  }
  return undefined;
}

export function AttachmentViewer({ attachment, onClose }: { attachment: Attachment; onClose: () => void }) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); };
  }, [onClose]);
  const dark = attachment.kind === 'xray';
  return (
    <div className="clm-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="clm-dialog" role="dialog" aria-modal="true" aria-label={attachment.name}>
        <div className="clm-card-head" style={{ marginBottom: 0 }}>
          <div>
            <p className="clm-h3">{attachment.name}</p>
            <p className="clm-meta">
              {attachment.date} · {attachment.origin}
            </p>
          </div>
          <button ref={closeRef} type="button" className="clm-btn clm-btn-sm" onClick={onClose}>
            Close
          </button>
        </div>
        {attachment.objectUrl ? (
          <img src={attachment.objectUrl} alt={attachment.name} style={{ maxWidth: '100%', borderRadius: 10 }} />
        ) : (
          <div
            className="clm-viewer-body"
            style={{ background: dark ? '#2b3436' : '#c9d1cf', color: dark ? '#fff' : '#2b3436' }}
          >
            Image preview
          </div>
        )}
      </div>
    </div>
  );
}

interface EvidenceLinkProps {
  id: string;
  label: string;
  claim: Claim;
}

/** A dotted "source" link that opens either an evidence popover or the viewer. */
export function EvidenceLink({ id, label, claim }: EvidenceLinkProps) {
  const [open, setOpen] = useState(false);
  const [viewer, setViewer] = useState<Attachment | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const isAttachment = id.startsWith('att-');

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const onClick = () => {
    if (isAttachment) {
      const att = resolveAttachment(claim, id) ?? {
        id,
        name: label,
        kind: 'document' as const,
        date: '',
        origin: `from ${PMS}` as const,
      };
      setViewer(att);
    } else {
      setOpen((v) => !v);
    }
  };

  const ev = EVIDENCE[id];

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button type="button" className="clm-src" onClick={onClick}>
        {label}
      </button>
      {open && ev ? (
        <div className="clm-popover" role="dialog" aria-label={ev.title} style={{ left: 0, top: 22 }}>
          <p className="clm-popover-title">{ev.title}</p>
          <p className="clm-popover-sum">{ev.summary}</p>
          {ev.table ? (
            <table className="clm-ev-table">
              <thead>
                <tr>
                  <th>Claim</th>
                  <th>Service date</th>
                  <th>Outcome</th>
                </tr>
              </thead>
              <tbody>
                {ev.table.map((r) => (
                  <tr key={r.claim}>
                    <td className="num">{r.claim}</td>
                    <td>{r.date}</td>
                    <td>{r.outcome}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </div>
      ) : null}
      {viewer ? <AttachmentViewer attachment={viewer} onClose={() => { setViewer(null); }} /> : null}
    </div>
  );
}
